import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../utils/api";

export interface CreditOrder {
    order_id: number;
    user_id: number;
    package_id: number;
    package_name: string;
    amount: number;
    currency: string;
    credits: number;
    bonus_credits: number;
    total_credits?: number;
    status: "PENDING" | "PAID" | "FAILED" | "REFUNDED";
    created_at: string;
    updated_at: string;
    user_name?: string;
    email?: string;
    refund_reason?: string | null;
    payment?: {
        transaction_id: number;
        provider: string;
        provider_tsx_id: string | null;
        status: string;
        amount: number;
        currency: string;
    } | null;
}

export interface CreditUser {
    user_id: number;
    user_name: string;
    email: string;
    smile_credits: number;
    smile_free_credits: boolean;
    verifySmile: boolean;
    role: string;
}

export interface RefundStats {
    totalRefunds: number;
    refundedAmount: number;
    pendingRefundable: number;
    refundRate: number;
}

export interface MyOrdersStats {
    totalSpent: number;
    currency: string;
    totalCreditsPurchased: number;
    paidOrders: number;
}

export interface CreditUsageEntry {
    ledger_id: number;
    user_id: number;
    service: string;
    order_id: number | null;
    transaction_type: string;
    credits: number;
    credits_abs: number;
    is_debit: boolean;
    balance_before: number;
    balance_after: number;
    remark: string;
    used_for: string;
    created_at: string;
    user_name: string;
    email: string;
    token: string | null;
}

export interface CreditUsageStats {
    totalEvents: number;
    uniqueClients: number;
    creditsUsed: number;
}

export interface CreditAuditLogEntry {
    log_id: number;
    target_user_id: number;
    admin_id: number;
    action: "ADD" | "REMOVE";
    credits: number;
    balance_before: number;
    balance_after: number;
    remark: string;
    created_at: string;
    target_user_name: string;
    target_user_email: string;
    admin_name: string;
    admin_email: string;
}

export interface CreditAuditLogsPagination {
    totalCount: number;
    currentPage: number;
    totalPages: number;
    limit: number;
}

export type ListPagination = CreditAuditLogsPagination;

interface PaymentStats {
    totalRevenue: number;
    totalTransactions: number;
    paidTransactions: number;
    uniqueCustomers: number;
}

interface PaymentState {
    myOrders: CreditOrder[];
    allOrders: CreditOrder[];
    stats: PaymentStats | null;
    creditUsers: CreditUser[];
    totalUserCredits: number;
    refunds: CreditOrder[];
    refundableOrders: CreditOrder[];
    refundStats: RefundStats | null;
    creditAuditLogs: CreditAuditLogEntry[];
    auditLogsPagination: CreditAuditLogsPagination | null;
    myOrdersPagination: ListPagination | null;
    allOrdersPagination: ListPagination | null;
    creditUsersPagination: ListPagination | null;
    refundsPagination: ListPagination | null;
    refundablePagination: ListPagination | null;
    myOrdersStats: MyOrdersStats | null;
    creditUsage: CreditUsageEntry[];
    creditUsagePagination: ListPagination | null;
    creditUsageStats: CreditUsageStats | null;
    creditUsageLoading: boolean;
    auditLogsLoading: boolean;
    checkoutLoading: boolean;
    verifyLoading: boolean;
    refundLoading: boolean;
    loading: boolean;
    error: string | null;
    verificationResult: any | null;
}

const initialState: PaymentState = {
    myOrders: [],
    allOrders: [],
    stats: null,
    creditUsers: [],
    totalUserCredits: 0,
    refunds: [],
    refundableOrders: [],
    refundStats: null,
    creditAuditLogs: [],
    auditLogsPagination: null,
    myOrdersPagination: null,
    allOrdersPagination: null,
    creditUsersPagination: null,
    refundsPagination: null,
    refundablePagination: null,
    myOrdersStats: null,
    creditUsage: [],
    creditUsagePagination: null,
    creditUsageStats: null,
    creditUsageLoading: false,
    auditLogsLoading: false,
    checkoutLoading: false,
    verifyLoading: false,
    refundLoading: false,
    loading: false,
    error: null,
    verificationResult: null,
};

export const createCheckoutSession = createAsyncThunk(
    "payment/createCheckout",
    async (
        payload: { package_id: number; service?: string } | number,
        { rejectWithValue }
    ) => {
        try {
            const body =
                typeof payload === "number"
                    ? { package_id: payload }
                    : payload;
            const res = await api.post(
                "/payment/stripe/create-checkout-session",
                body,
                { withCredentials: true }
            );
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data?.message || "Checkout failed"
            );
        }
    }
);

export const verifyCheckoutSession = createAsyncThunk(
    "payment/verifySession",
    async (session_id: string, { rejectWithValue }) => {
        try {
            const res = await api.get(
                `/payment/stripe/verify-session/${session_id}`,
                { withCredentials: true }
            );
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data?.message || "Verification failed"
            );
        }
    }
);

export const fetchMyOrders = createAsyncThunk(
    "payment/fetchMyOrders",
    async (
        params:
            | {
                  page?: number;
                  limit?: number;
                  service?: string;
                  status?: string;
              }
            | undefined,
        { rejectWithValue }
    ) => {
        try {
            const res = await api.get("/payment/orders/my", {
                params,
                withCredentials: true,
            });
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to fetch orders"
            );
        }
    }
);

export const fetchAllOrders = createAsyncThunk(
    "payment/fetchAllOrders",
    async (
        params:
            | { status?: string; search?: string; page?: number; limit?: number }
            | undefined,
        { rejectWithValue }
    ) => {
        try {
            const res = await api.get("/payment/orders/all", {
                params,
                withCredentials: true,
            });
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to fetch orders"
            );
        }
    }
);

export const cancelPendingOrder = createAsyncThunk(
    "payment/cancelOrder",
    async (order_id: number, { rejectWithValue }) => {
        try {
            const res = await api.post(
                `/payment/orders/${order_id}/cancel`,
                {},
                { withCredentials: true }
            );
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to cancel order"
            );
        }
    }
);

export const fetchCreditUsers = createAsyncThunk(
    "payment/fetchCreditUsers",
    async (
        params: { search?: string; page?: number; limit?: number } | undefined,
        { rejectWithValue }
    ) => {
        try {
            const res = await api.get("/payment/credits/users", {
                params,
                withCredentials: true,
            });
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to fetch users"
            );
        }
    }
);

export const adjustUserCredits = createAsyncThunk(
    "payment/adjustCredits",
    async (
        data: {
            user_id: number;
            credits: number;
            action: "add" | "remove";
            remark?: string;
        },
        { rejectWithValue }
    ) => {
        try {
            const res = await api.post("/payment/credits/adjust", data, {
                withCredentials: true,
            });
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to adjust credits"
            );
        }
    }
);

export const fetchRefundOverview = createAsyncThunk(
    "payment/fetchRefunds",
    async (
        params:
            | {
                  search?: string;
                  refundsPage?: number;
                  refundablePage?: number;
                  limit?: number;
              }
            | undefined,
        { rejectWithValue }
    ) => {
        try {
            const res = await api.get("/payment/refunds", {
                params,
                withCredentials: true,
            });
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to fetch refunds"
            );
        }
    }
);

export const processOrderRefund = createAsyncThunk(
    "payment/processRefund",
    async (
        data: { order_id: number; reason: string },
        { rejectWithValue }
    ) => {
        try {
            const res = await api.post(
                `/payment/refunds/${data.order_id}`,
                { reason: data.reason },
                { withCredentials: true }
            );
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to process refund"
            );
        }
    }
);

export const fetchCreditAuditLogs = createAsyncThunk(
    "payment/fetchCreditAuditLogs",
    async (
        params:
            | {
                  page?: number;
                  limit?: number;
                  search?: string;
                  action?: string;
              }
            | undefined,
        { rejectWithValue }
    ) => {
        try {
            const res = await api.get("/payment/credits/audit-logs", {
                params,
                withCredentials: true,
            });
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data?.message || "Failed to fetch credit audit logs"
            );
        }
    }
);

export const fetchCreditUsageHistory = createAsyncThunk(
    "payment/fetchCreditUsageHistory",
    async (
        params:
            | {
                  page?: number;
                  limit?: number;
                  search?: string;
                  type?: string;
                  service?: string;
              }
            | undefined,
        { rejectWithValue }
    ) => {
        try {
            const res = await api.get("/payment/credits/usage-history", {
                params,
                withCredentials: true,
            });
            return res.data.data;
        } catch (err: any) {
            return rejectWithValue(
                err.response?.data?.message ||
                    "Failed to fetch credit usage history"
            );
        }
    }
);

const paymentSlice = createSlice({
    name: "payment",
    initialState,
    reducers: {
        clearPaymentError: (state) => {
            state.error = null;
        },
        clearVerificationResult: (state) => {
            state.verificationResult = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(createCheckoutSession.pending, (state) => {
                state.checkoutLoading = true;
                state.error = null;
            })
            .addCase(createCheckoutSession.fulfilled, (state) => {
                state.checkoutLoading = false;
            })
            .addCase(createCheckoutSession.rejected, (state, action) => {
                state.checkoutLoading = false;
                state.error = action.payload as string;
            })

            .addCase(verifyCheckoutSession.pending, (state) => {
                state.verifyLoading = true;
                state.error = null;
            })
            .addCase(verifyCheckoutSession.fulfilled, (state, action) => {
                state.verifyLoading = false;
                state.verificationResult = action.payload;
            })
            .addCase(verifyCheckoutSession.rejected, (state, action) => {
                state.verifyLoading = false;
                state.error = action.payload as string;
            })

            .addCase(fetchMyOrders.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchMyOrders.fulfilled, (state, action) => {
                state.loading = false;
                state.myOrders = action.payload.orders;
                state.myOrdersStats = action.payload.stats ?? null;
                state.myOrdersPagination = {
                    totalCount: action.payload.totalCount,
                    currentPage: action.payload.currentPage,
                    totalPages: action.payload.totalPages,
                    limit: action.payload.limit,
                };
            })
            .addCase(fetchMyOrders.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            .addCase(fetchAllOrders.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchAllOrders.fulfilled, (state, action) => {
                state.loading = false;
                state.allOrders = action.payload.orders;
                state.stats = action.payload.stats;
                state.allOrdersPagination = {
                    totalCount: action.payload.totalCount,
                    currentPage: action.payload.currentPage,
                    totalPages: action.payload.totalPages,
                    limit: action.payload.limit,
                };
            })
            .addCase(fetchAllOrders.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            .addCase(fetchCreditUsers.pending, (state) => {
                state.loading = true;
            })
            .addCase(fetchCreditUsers.fulfilled, (state, action) => {
                state.loading = false;
                state.creditUsers = action.payload.users;
                state.totalUserCredits = action.payload.totalCredits;
                state.creditUsersPagination = {
                    totalCount: action.payload.totalCount,
                    currentPage: action.payload.currentPage,
                    totalPages: action.payload.totalPages,
                    limit: action.payload.limit,
                };
            })
            .addCase(fetchCreditUsers.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            .addCase(adjustUserCredits.fulfilled, (state, action) => {
                const index = state.creditUsers.findIndex(
                    (u) => u.user_id === action.payload.user_id
                );
                if (index !== -1) {
                    state.creditUsers[index].smile_credits =
                        action.payload.smile_credits;
                }
            })

            .addCase(fetchRefundOverview.pending, (state) => {
                state.refundLoading = true;
                state.error = null;
            })
            .addCase(fetchRefundOverview.fulfilled, (state, action) => {
                state.refundLoading = false;
                state.refunds = action.payload.refunds;
                state.refundableOrders = action.payload.refundableOrders;
                state.refundStats = action.payload.stats;
                state.refundsPagination = action.payload.refundsPagination;
                state.refundablePagination = action.payload.refundablePagination;
            })
            .addCase(fetchRefundOverview.rejected, (state, action) => {
                state.refundLoading = false;
                state.error = action.payload as string;
            })

            .addCase(processOrderRefund.pending, (state) => {
                state.refundLoading = true;
                state.error = null;
            })
            .addCase(processOrderRefund.fulfilled, (state) => {
                state.refundLoading = false;
            })
            .addCase(processOrderRefund.rejected, (state, action) => {
                state.refundLoading = false;
                state.error = action.payload as string;
            })

            .addCase(fetchCreditAuditLogs.pending, (state) => {
                state.auditLogsLoading = true;
                state.error = null;
            })
            .addCase(fetchCreditAuditLogs.fulfilled, (state, action) => {
                state.auditLogsLoading = false;
                state.creditAuditLogs = action.payload.logs;
                state.auditLogsPagination = {
                    totalCount: action.payload.totalCount,
                    currentPage: action.payload.currentPage,
                    totalPages: action.payload.totalPages,
                    limit: action.payload.limit,
                };
            })
            .addCase(fetchCreditAuditLogs.rejected, (state, action) => {
                state.auditLogsLoading = false;
                state.error = action.payload as string;
            })

            .addCase(fetchCreditUsageHistory.pending, (state) => {
                state.creditUsageLoading = true;
                state.error = null;
            })
            .addCase(fetchCreditUsageHistory.fulfilled, (state, action) => {
                state.creditUsageLoading = false;
                state.creditUsage = action.payload.history;
                state.creditUsageStats = action.payload.stats ?? null;
                state.creditUsagePagination = {
                    totalCount: action.payload.totalCount,
                    currentPage: action.payload.currentPage,
                    totalPages: action.payload.totalPages,
                    limit: action.payload.limit,
                };
            })
            .addCase(fetchCreditUsageHistory.rejected, (state, action) => {
                state.creditUsageLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const { clearPaymentError, clearVerificationResult } =
    paymentSlice.actions;

export default paymentSlice.reducer;
